import { useEffect, useState } from 'react';
import { Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { schedulerAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtTimestamp(isoString) {
  if (!isoString) return '-';
  const then = new Date(isoString);
  if (Number.isNaN(then.getTime())) return isoString;
  return then.toLocaleString('en-GB');
}

function fmtDuration(ms) {
  if (ms === null || ms === undefined) return '-';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function fmtInterval(seconds) {
  if (seconds === null || seconds === undefined) return 'cron';
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

// Mini duration-history bars: one series (this job's own duration over its
// last 10 runs), so a single neutral hue is correct -- no categorical
// palette needed for one series (dataviz skill). Height is relative to the
// job's own max in the window, not a fixed scale, since jobs range from
// ms to minutes.
function DurationSparkline({ history }) {
  if (!history || history.length === 0) {
    return <span className="text-muted-foreground text-xs">no runs yet</span>;
  }
  const ordered = [...history].reverse(); // oldest -> newest, left to right
  const max = Math.max(...ordered.map((r) => r.duration_ms || 0), 1);
  return (
    <div className="flex items-end gap-0.5 h-6" title="last 10 runs, oldest to newest">
      {ordered.map((r, i) => {
        const h = Math.max(2, Math.round(((r.duration_ms || 0) / max) * 24));
        return (
          <div
            key={i}
            className={`w-1.5 rounded-sm ${r.outcome === 'error' ? 'bg-destructive' : 'bg-primary/60'}`}
            style={{ height: `${h}px` }}
            title={`${fmtDuration(r.duration_ms)} (${r.outcome}) at ${fmtTimestamp(r.started_at)}`}
          />
        );
      })}
    </div>
  );
}

function fmtAge(seconds) {
  if (seconds === null || seconds === undefined) return '-';
  if (seconds < 90) return `${Math.round(seconds)}s ago`;
  if (seconds < 5400) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 172800) return `${(seconds / 3600).toFixed(1)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}

// One line naming where the job list came from and whether the scheduler
// process is alive. In production (API + scheduler as separate services)
// this is the ONLY place that answers "is the scheduler running at all".
export function SchedulerHeartbeatLine({ source, heartbeat }) {
  if (source !== 'heartbeat' || !heartbeat) {
    return (
      <div className="text-xs text-muted-foreground" data-testid="scheduler-heartbeat-line">
        Scheduler runs in this process.
      </div>
    );
  }
  if (!heartbeat.present) {
    return (
      <div className="text-xs flex items-center gap-1 text-destructive font-medium" data-testid="scheduler-heartbeat-line">
        <AlertTriangle className="h-3 w-3" />
        Scheduler service: NO HEARTBEAT EVER RECORDED. No background job is running.
      </div>
    );
  }
  if (!heartbeat.alive) {
    return (
      <div className="text-xs flex items-center gap-1 text-destructive font-medium" data-testid="scheduler-heartbeat-line">
        <AlertTriangle className="h-3 w-3" />
        Scheduler service DOWN: last heartbeat {fmtAge(heartbeat.age_seconds)} ({fmtTimestamp(heartbeat.last_beat_at)}) from {heartbeat.hostname || '?'}.
      </div>
    );
  }
  return (
    <div className="text-xs text-muted-foreground" data-testid="scheduler-heartbeat-line">
      Scheduler service alive: heartbeat {fmtAge(heartbeat.age_seconds)} from {heartbeat.hostname || '?'}
      {heartbeat.build_commit ? ` · build ${String(heartbeat.build_commit).slice(0, 8)}` : ''}
      {heartbeat.started_at ? ` · up since ${fmtTimestamp(heartbeat.started_at)}` : ''}
    </div>
  );
}

// Diagnostics card: last run + last-10 duration history for every job
// registered with APScheduler at boot, with a stacking-risk flag (last
// duration > half the job's own interval -- a job that outruns its
// interval stacks with the next fire, the 2026-08-15 slowdown's
// mechanism). See routes/scheduler.py's job-durations endpoint and
// services/scheduler_job_runs.py for how the run history is captured.
export function SchedulerJobDurationsPanel() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // GET job-durations returns 503 "Scheduler not initialized" during the
  // boot window before set_scheduler() runs (routes/scheduler.py) -- was
  // previously indistinguishable from the empty-array "no jobs" state,
  // rendering the misleading "No jobs registered." (Prompt 21 FIX 5).
  const [schedulerStarting, setSchedulerStarting] = useState(false);
  // Self-hosted split: this API process registers no jobs, the job list
  // and liveness come from the scheduler service's heartbeat
  // (services/scheduler_heartbeat.py). `source` says which one we got.
  const [source, setSource] = useState('local');
  const [heartbeat, setHeartbeat] = useState(null);
  const [gatedJobs, setGatedJobs] = useState([]);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await schedulerAPI.getJobDurations();
      setJobs(data.jobs || []);
      setSource(data.source || 'local');
      setHeartbeat(data.heartbeat || null);
      setGatedJobs(data.gated_jobs || []);
      setSchedulerStarting(false);
    } catch (err) {
      if (err?.response?.status === 503) {
        setSchedulerStarting(true);
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const riskCount = jobs.filter((j) => j.stacking_risk).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          Scheduler Job Durations
          {riskCount > 0 && (
            <Badge variant="destructive" data-testid="scheduler-durations-risk-badge">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {riskCount} at risk of stacking
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Last run and last-10 duration history for every job registered with the scheduler.
          A job flagged in red took longer than half its own interval on its last run --
          it risks stacking with its next scheduled fire, which is how a single slow job can
          make the whole app feel down.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button
          data-testid="scheduler-durations-refresh"
          size="sm"
          variant="outline"
          onClick={refresh}
          disabled={loading}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Refresh
        </Button>

        {loaded && <SchedulerHeartbeatLine source={source} heartbeat={heartbeat} />}

        {loaded && gatedJobs.length > 0 && (
          <div className="text-xs border border-destructive/40 bg-destructive/10 rounded-md p-2 space-y-1" data-testid="scheduler-gated-jobs">
            <div className="font-medium flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 text-destructive" />
              {gatedJobs.length} job{gatedJobs.length === 1 ? '' : 's'} NOT registered at scheduler start
            </div>
            {gatedJobs.map((g) => (
              <div key={g.job_id} data-testid={`scheduler-gated-${g.job_id}`}>
                <span className="font-mono">{g.job_id}</span>: {g.reason}
              </div>
            ))}
          </div>
        )}

        {loaded && jobs.length === 0 && (
          <div className="text-xs text-muted-foreground" data-testid="scheduler-durations-empty">
            {schedulerStarting
              ? 'Scheduler starting…'
              : (source === 'heartbeat' && heartbeat && !heartbeat.present
                ? 'No jobs registered here and no scheduler heartbeat has ever been recorded: the scheduler service is not running (or runs a build without the heartbeat).'
                : 'No jobs registered.')}
          </div>
        )}

        {jobs.length > 0 && (
          <div className="overflow-x-auto border rounded-lg max-h-[32rem] overflow-y-auto" data-testid="scheduler-durations-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50 sticky top-0">
                  <th className="text-left px-2 py-1 font-medium">Job</th>
                  <th className="text-left px-2 py-1 font-medium">Interval</th>
                  <th className="text-left px-2 py-1 font-medium">Last run</th>
                  <th className="text-left px-2 py-1 font-medium">Duration</th>
                  <th className="text-left px-2 py-1 font-medium">Outcome</th>
                  <th className="text-left px-2 py-1 font-medium">Next run</th>
                  <th className="text-left px-2 py-1 font-medium">History</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr
                    key={job.job_id}
                    className={`border-b last:border-0 ${job.stacking_risk ? 'bg-destructive/10' : ''}`}
                    data-testid={`scheduler-duration-row-${job.job_id}`}
                  >
                    <td className="px-2 py-1 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {job.stacking_risk && <AlertTriangle className="h-3 w-3 text-destructive shrink-0" />}
                        <span>{job.job_name}</span>
                      </div>
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">{fmtInterval(job.interval_seconds)}</td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      {job.last_run ? fmtTimestamp(job.last_run.started_at) : 'never'}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      {job.last_run ? fmtDuration(job.last_run.duration_ms) : '-'}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      {job.last_run ? (
                        <Badge variant={job.last_run.outcome === 'error' ? 'destructive' : 'secondary'}>
                          {job.last_run.outcome}
                        </Badge>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">{fmtTimestamp(job.next_run)}</td>
                    <td className="px-2 py-1">
                      <DurationSparkline history={job.history} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
